import React, { useState, useContext, useEffect } from "react"
import PropTypes from "prop-types"
import { gql, useQuery } from "@apollo/client"
import { Field, useFormikContext } from "formik"

// PR
import { MultiSelect } from "primereact/multiselect"
import { FormContext } from "../../../context/formContext"
import { UserContext } from "../../../context/userContext"
import { LoaderSmall } from "../../Loaders"

import styles from "../../../styles/Form.module.css"

const ProgramIdentityId = ({ field, block, index }) => {
  const [activePrograms, setActivePrograms] = useState([])
  const [inactivePrograms, setInactivePrograms] = useState([])

  const form = useContext(FormContext)
  const userCtx = useContext(UserContext)
  const formik = useFormikContext()

  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  const safeArray = (value) => (Array.isArray(value) ? value : [])

  const checkIfRequired = () => {
    try {
      return JSON.parse(field.field_validation.required[0])
    } catch (error) {
      return false
    }
  }

  const isRequired = checkIfRequired()

  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(field)

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(field)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // -----------------------------
  // 🔧 Remapea valores seleccionados según el key
  // -----------------------------
  const remapByKey = (selectedList, optionsList) => {
    const safeSelected = safeArray(selectedList)

    return safeSelected
      .map((item) => {
        const match = optionsList.find((opt) => opt.key === item.key)
        if (!match) {
          console.warn(
            "[ProgramIdentityId] Missing option for key:",
            item.key,
            "Selected item was:",
            item
          )
        }
        return match || null
      })
      .filter(Boolean)
  }

  // -----------------------------
  // GraphQL Query
  // -----------------------------
  const GET_PROGRAMS = gql`
    query getPrograms($staff_id: ID!, $organization_id: ID!) {
      programs: programsByOrganization(
        staff_id: $staff_id
        organization_id: $organization_id
      ) {
        key: program_identity_id
        label: program_id_name
        status: program_id_status
      }
    }
  `

  const { loading } = useQuery(GET_PROGRAMS, {
    fetchPolicy: "network-only",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
    skip: !userCtx,
    onCompleted: ({ programs }) => {
      const activeProgramsList =
        programs
          .filter((program) => program.status === "ACTIVE")
          .map((program) => ({
            key: program.key,
            label: program.label,
          })) || []

      const inactiveProgramsList =
        programs
          .filter((program) => program.status === "INACTIVE")
          .map((program) => ({
            key: program.key,
            label: program.label,
          })) || []

      setActivePrograms(activeProgramsList)
      setInactivePrograms(inactiveProgramsList)
    },
  })

  // -----------------------------
  // Handlers
  // -----------------------------
  const handleSelectProgramIds = (value) => {
    if (value === null) {
      formik.setFieldValue(field.field_name, [])
      return
    }

    const fieldCurrentValue = safeArray(formik.values[field.field_name])

    const currentInactive = fieldCurrentValue.filter((item) =>
      inactivePrograms.some((inactiveItem) => inactiveItem.key === item.key)
    )

    const newValue = [...currentInactive, ...safeArray(value)]
    formik.setFieldValue(field.field_name, newValue)
  }

  const handleDeselectInactiveProgramIds = (value) => {
    const safeVal = safeArray(value)

    if (safeVal.length === 0) {
      const selectedPrograms = safeArray(
        formik.values[field.field_name]
      ).filter((item) =>
        activePrograms.find((activeItem) => activeItem.key === item.key)
      )

      formik.setFieldValue(field.field_name, selectedPrograms)
      return
    }

    const fieldCurrentValue = safeArray(formik.values[field.field_name])

    const currentActive = fieldCurrentValue.filter((item) =>
      activePrograms.some((activeItem) => activeItem.key === item.key)
    )

    const updated = [...currentActive, ...safeVal]
    setInactivePrograms(safeVal)
    formik.setFieldValue(field.field_name, updated)
  }

  // -----------------------------
  // Render MultiSelect (with remap)
  // -----------------------------
  const handleMultiSelectRender = (formikField) => {
    const currentValues = safeArray(formikField?.value)

    // Clean remapping based on key
    const currentActiveSelectedPrograms = remapByKey(
      currentValues,
      activePrograms
    )
    const currentInactiveSelectedPrograms = remapByKey(
      currentValues,
      inactivePrograms
    )

    const renderInactivePrograms = () => {
      if (!form?.editMode || currentInactiveSelectedPrograms.length === 0)
        return null

      return (
        <div>
          <label className={styles.label}>Inactive Programs ID(s)</label>
          <div className={styles.description}>
            Inactive programs can only be deselected.
          </div>

          <MultiSelect
            placeholder="Unselect only"
            id={field.field_name}
            name={formikField.name}
            value={currentInactiveSelectedPrograms}
            onChange={(e) => handleDeselectInactiveProgramIds(e.value)}
            onBlur={(e) => formik.handleBlur(e)}
            disabled={isDisabled}
            options={inactivePrograms}
          />
        </div>
      )
    }

    return (
      <>
        <MultiSelect
          placeholder="Please select..."
          id={field.field_name}
          name={formikField.name}
          value={currentActiveSelectedPrograms}
          onChange={(e) => handleSelectProgramIds(e.value)}
          onBlur={(e) => formik.handleBlur(e)}
          disabled={isDisabled}
          options={activePrograms}
        />

        {renderInactivePrograms()}
      </>
    )
  }

  if (loading) return <LoaderSmall />

  return (
    <div
      className={styles.field}
      style={{
        order: index,
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>

      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}

      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, meta }) => (
            <>
              {handleMultiSelectRender(formikField)}
              {meta.touched && meta.error && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}

ProgramIdentityId.propTypes = {
  field: PropTypes.object.isRequired,
  block: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
}

export default ProgramIdentityId
